import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { Task, TaskStatus } from "../../lib/types";

interface ListAgentTasksParams {
  /** Agent node to query. */
  nodeId: string | undefined;
  status?: TaskStatus;
  /** Include `done` tasks. */
  includeDone?: boolean;
  /** Include inbox (untriaged) tasks. */
  includeInbox?: boolean;
  /** Sprint 14g — include soft-archived tasks (for the "Show archived" filter). */
  includeArchived?: boolean;
}

/**
 * An agent's tasks (Sprint 26.0). Wraps `list_tasks` (queue lens — flat,
 * ranked, #1 = current). Polls at 30s so tasks created via MCP appear without a
 * manual refresh.
 */
export function useListAgentTasks({ nodeId, status, includeDone, includeInbox, includeArchived }: ListAgentTasksParams) {
  return useQuery<Task[]>({
    queryKey: [
      "agent-tasks",
      nodeId ?? "",
      status ?? "all",
      includeDone ?? false,
      includeInbox ?? false,
      includeArchived ?? false,
    ],
    queryFn: () => {
      if (!nodeId) return [];
      return mcpClient.listTasks(nodeId, {
        status,
        include_done: includeDone,
        include_inbox: includeInbox,
        include_archived: includeArchived,
      });
    },
    enabled: !!nodeId,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}
